import React, { useEffect } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { useI18n } from "@/i18n";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Phone,
  MessageCircle,
  Clock,
  MapPin,
  Star,
  ShieldCheck,
  Car,
  Plane,
  Ship,
  ChevronRight,
  Route as RouteIcon,
} from "lucide-react";
import { LOCATIONS } from "@/data/locationsContent";
import { sanitizeHtml } from "@/lib/sanitize";

const CAR_IMG =
  "https://customer-assets.emergentagent.com/job_gallura-auto-rental/artifacts/vfttlow5_1782508931419.jpg.jpg";

const ROUTE_ICON = {
  airport: <Plane className="w-4 h-4" />,
  port: <Ship className="w-4 h-4" />,
  tour: <Car className="w-4 h-4" />,
  transfer: <RouteIcon className="w-4 h-4" />,
};

const ROUTE_TYPE_LABEL_IT = {
  airport: "Transfer aeroporto",
  port: "Transfer porto",
  tour: "Tour privato",
  transfer: "Transfer",
};
const ROUTE_TYPE_LABEL_EN = {
  airport: "Airport transfer",
  port: "Port transfer",
  tour: "Private tour",
  transfer: "Transfer",
};

/**
 * Generic location landing page.
 * Reads content from LOCATIONS dict via useParams("slug").
 * Injects meta tags + JSON-LD (Service, FAQPage, BreadcrumbList) on mount.
 */
export default function LocationLanding() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\/ncc-/, "");
  const { lang, setLang } = useI18n();
  const loc = LOCATIONS[slug];

  const c = loc ? (lang === "it" ? loc.it : loc.en) : null;
  const routeLabel = lang === "it" ? ROUTE_TYPE_LABEL_IT : ROUTE_TYPE_LABEL_EN;
  const pageUrl = loc ? `https://galluradrive.it${loc.urlPath}` : "";
  const cityName = c ? c.h1.split("—")[0].trim() : "";

  useEffect(() => {
    if (!loc || !c) return;
    document.title = c.metaTitle;

    const upsertMeta = (attr, key, value) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };
    const upsertLink = (rel, hrefValue, hreflang) => {
      const selector = hreflang
        ? `link[rel="${rel}"][hreflang="${hreflang}"]`
        : `link[rel="${rel}"]`;
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        if (hreflang) el.setAttribute("hreflang", hreflang);
        document.head.appendChild(el);
      }
      el.setAttribute("href", hrefValue);
    };

    upsertMeta("name", "description", c.metaDescription);
    upsertMeta(
      "name",
      "keywords",
      `ncc ${loc.slug}, taxi ${loc.slug}, taxi ncc ${loc.slug}, autista privato ${loc.slug}, transfer ${loc.slug}, chauffeur ${loc.slug}, private driver ${loc.slug}, ncc gallura, ncc costa smeralda, ncc olbia, ncc santa teresa gallura, ncc palau, ncc cannigione, ncc baja sardinia, transfer bonifacio sardegna, transfer aeroporto olbia, taxi porto cervo, autista privato costa smeralda, taxi privato sardegna, chauffeur gallura`
    );
    upsertMeta("property", "og:title", c.metaTitle);
    upsertMeta("property", "og:description", c.metaDescription);
    upsertMeta("property", "og:url", pageUrl);
    upsertMeta("property", "og:image", CAR_IMG);
    upsertMeta("property", "og:type", "website");
    upsertMeta("name", "twitter:title", c.metaTitle);
    upsertMeta("name", "twitter:description", c.metaDescription);
    upsertMeta("name", "twitter:image", CAR_IMG);
    upsertLink("canonical", pageUrl);
    upsertLink("alternate", pageUrl, "it");
    upsertLink("alternate", `${pageUrl}?lang=en`, "en");
    upsertLink("alternate", pageUrl, "x-default");

    const buildLd = (id, data) => {
      let el = document.getElementById(id);
      if (!el) {
        el = document.createElement("script");
        el.type = "application/ld+json";
        el.id = id;
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(data);
    };

    const ldPrefix = `ld-${loc.slug}`;

    buildLd(`${ldPrefix}-service`, {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "NCC — Noleggio Con Conducente",
      name: c.metaTitle,
      description: c.metaDescription,
      provider: {
        "@type": "LocalBusiness",
        name: "Gallura Drive",
        telephone: "+39 347 001 1246",
        image: CAR_IMG,
        priceRange: "€€",
        address: {
          "@type": "PostalAddress",
          addressLocality: cityName,
          addressRegion: "SS",
          postalCode: loc.geo.postal,
          addressCountry: "IT",
        },
        geo: {
          "@type": "GeoCoordinates",
          latitude: loc.geo.lat,
          longitude: loc.geo.lng,
        },
      },
      areaServed: [
        { "@type": "City", name: cityName },
        { "@type": "City", name: "Olbia" },
        { "@type": "City", name: "Porto Cervo" },
        { "@type": "Place", name: "Costa Smeralda" },
        { "@type": "City", name: "Santa Teresa Gallura" },
        { "@type": "City", name: "Palau" },
        { "@type": "City", name: "Cannigione" },
        { "@type": "City", name: "Baja Sardinia" },
        { "@type": "City", name: "Bonifacio" },
      ],
      offers: {
        "@type": "AggregateOffer",
        priceCurrency: "EUR",
        lowPrice: "20",
        highPrice: "280",
        offerCount: String(c.routes.length),
      },
    });

    buildLd(`${ldPrefix}-faq`, {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: c.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });

    buildLd(`${ldPrefix}-breadcrumb`, {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Gallura Drive", item: "https://galluradrive.it/" },
        { "@type": "ListItem", position: 2, name: cityName, item: pageUrl },
      ],
    });

    return () => {
      if (!loc) return;
      [`ld-${loc.slug}-service`, `ld-${loc.slug}-faq`, `ld-${loc.slug}-breadcrumb`].forEach((id) => {
        const n = document.getElementById(id);
        if (n) n.remove();
      });
    };
  }, [c, loc, lang, pageUrl, cityName]);

  // Unknown slug -> back to homepage (after all hooks are called)
  if (!loc || !c) return <Navigate to="/" replace />;

  // Related landings (sibling internal links for SEO)
  const related = Object.values(LOCATIONS).filter((l) => l.slug !== loc.slug);

  return (
    <div
      data-testid={`location-page-${loc.slug}`}
      className="min-h-screen bg-[#F9F7F3]"
    >
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link
            to="/"
            data-testid="loc-back-home"
            className="flex items-center gap-2 text-slate-700 hover:brand-text transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-display text-lg">Gallura Drive</span>
          </Link>
          <div className="flex items-center gap-3">
            <button
              data-testid="loc-lang-toggle"
              onClick={() => setLang(lang === "it" ? "en" : "it")}
              className="text-sm text-slate-600 hover:brand-text uppercase tracking-wider"
            >
              {lang === "it" ? "EN" : "IT"}
            </button>
            <a
              href="tel:+393470011246"
              data-testid="loc-header-call"
              className="hidden sm:inline-flex items-center gap-2 brand-bg text-white rounded-full px-4 py-2 text-sm hover:opacity-90 transition-opacity"
            >
              <Phone className="w-4 h-4" />
              +39 347 001 1246
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 py-14 lg:py-20 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="uppercase tracking-widest text-xs text-slate-500 mb-4">
            {c.eyebrow}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl brand-text leading-tight">
            {c.h1}
          </h1>
          <p className="mt-6 text-slate-700 leading-relaxed text-lg max-w-xl">
            {c.lead}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/#booking" data-testid="loc-cta-book">
              <Button className="brand-bg hover:opacity-90 text-white rounded-full px-6 h-12">
                {lang === "it" ? "Prenota ora" : "Book now"}
              </Button>
            </Link>
            <a
              href="https://wa.me/393470011246"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="loc-cta-whatsapp"
              className="inline-flex items-center gap-2 rounded-full px-6 h-12 border border-slate-300 hover:border-slate-500 transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </a>
          </div>

          <ul className="mt-10 grid grid-cols-2 gap-4 text-sm text-slate-700">
            <li className="flex items-start gap-2">
              <Clock className="w-4 h-4 mt-0.5 brand-text" />
              {lang === "it" ? "Disponibile 24/7" : "Available 24/7"}
            </li>
            <li className="flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 mt-0.5 brand-text" />
              {lang === "it" ? "Autista NCC autorizzato" : "Licensed NCC driver"}
            </li>
            <li className="flex items-start gap-2">
              <Car className="w-4 h-4 mt-0.5 brand-text" />
              {lang === "it" ? "Chrysler 300C · fino a 4 pax" : "Chrysler 300C · up to 4 pax"}
            </li>
            <li className="flex items-start gap-2">
              <Star className="w-4 h-4 mt-0.5 brand-text" />
              {lang === "it" ? "Recensioni verificate" : "Verified reviews"}
            </li>
          </ul>
        </div>

        <div className="relative">
          <img
            src={CAR_IMG}
            alt={`Chrysler 300C Touring blu — ${cityName}`}
            className="w-full rounded-2xl shadow-xl object-cover aspect-[4/3]"
            loading="eager"
          />
          <div className="absolute -bottom-5 left-6 right-6 bg-white rounded-xl shadow-lg px-5 py-4 flex items-center gap-3">
            <MapPin className="w-5 h-5 brand-text" />
            <div className="text-sm">
              <div className="font-medium text-slate-800">{cityName}</div>
              <div className="text-slate-500">{c.zone}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="max-w-4xl mx-auto px-6 py-14">
        <h2 className="font-display text-3xl brand-text mb-6">{c.introTitle}</h2>
        <div
          className="prose prose-slate max-w-none text-slate-700 leading-relaxed space-y-4"
          dangerouslySetInnerHTML={sanitizeHtml(c.intro)}
        />
      </section>

      {/* Routes */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="uppercase tracking-widest text-xs text-slate-500 mb-2">
            {lang === "it" ? "Tratte più richieste" : "Most requested routes"}
          </div>
          <h2 className="font-display text-3xl brand-text mb-10">
            {lang === "it"
              ? `Prezzi indicativi · ${cityName}`
              : `Indicative prices · ${cityName}`}
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {c.routes.map((r) => (
              <div
                key={r.title}
                data-testid={`loc-route-card-${r.title.slice(0, 20)}`}
                className="border border-slate-200 rounded-xl p-6 bg-[#F9F7F3] hover:border-slate-400 transition-colors"
              >
                <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-widest mb-3">
                  {ROUTE_ICON[r.type] || ROUTE_ICON.transfer}
                  {routeLabel[r.type] || routeLabel.transfer}
                </div>
                <div className="font-display text-lg brand-text mb-1">
                  {r.title}
                </div>
                <div className="text-sm text-slate-600 mb-4">{r.desc}</div>
                <div className="flex items-baseline justify-between border-t border-slate-200 pt-3">
                  <span className="text-xs text-slate-500">
                    {lang === "it" ? "da" : "from"}
                  </span>
                  <span className="font-display text-2xl brand-text">
                    {r.price}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link to="/#booking" data-testid="loc-routes-book">
              <Button className="brand-bg hover:opacity-90 text-white rounded-full px-8 h-12">
                {lang === "it" ? "Prenota ora" : "Book now"}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="font-display text-3xl brand-text mb-10">
          {lang === "it"
            ? "Perché scegliere Gallura Drive"
            : "Why choose Gallura Drive"}
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              icon: <Clock className="w-5 h-5" />,
              titleIt: "Puntualità garantita",
              titleEn: "Guaranteed punctuality",
              descIt:
                "Monitoriamo il tuo volo, traghetto o orario di partenza in tempo reale. Ti aspettiamo senza costi extra fino a 60 minuti.",
              descEn:
                "We track your flight, ferry or departure in real time. We wait at no extra cost for up to 60 minutes.",
            },
            {
              icon: <ShieldCheck className="w-5 h-5" />,
              titleIt: "Tariffe trasparenti",
              titleEn: "Transparent rates",
              descIt:
                "Prezzo concordato prima della corsa. Nessun tassametro, nessun supplemento nascosto, IVA inclusa.",
              descEn:
                "Price agreed before the ride. No taximeter, no hidden surcharges, VAT included.",
            },
            {
              icon: <Star className="w-5 h-5" />,
              titleIt: "Comfort superiore",
              titleEn: "Superior comfort",
              descIt:
                "Chrysler 300C Touring blu: interni in pelle, aria condizionata, Wi-Fi, acqua omaggio a bordo.",
              descEn:
                "Blue Chrysler 300C Touring: leather interiors, A/C, Wi-Fi, complimentary water on board.",
            },
          ].map((w) => (
            <div
              key={w.titleEn}
              className="rounded-xl border border-slate-200 bg-white p-6"
            >
              <div className="w-10 h-10 rounded-full brand-bg text-white grid place-items-center mb-4">
                {w.icon}
              </div>
              <div className="font-display text-lg brand-text mb-2">
                {lang === "it" ? w.titleIt : w.titleEn}
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">
                {lang === "it" ? w.descIt : w.descEn}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <div className="uppercase tracking-widest text-xs text-slate-500 mb-2">
            FAQ
          </div>
          <h2 className="font-display text-3xl brand-text mb-8">
            {lang === "it"
              ? `Domande frequenti · NCC ${cityName}`
              : `FAQ · Private driver in ${cityName}`}
          </h2>
          <div className="space-y-4">
            {c.faq.map((f) => (
              <details
                key={f.q}
                data-testid={`loc-faq-${f.q.slice(0, 20)}`}
                className="group border border-slate-200 rounded-xl bg-[#F9F7F3] px-6 py-4 open:bg-white transition-colors"
              >
                <summary className="cursor-pointer font-display text-lg brand-text list-none flex items-start justify-between gap-4">
                  <span>{f.q}</span>
                  <ChevronRight className="w-5 h-5 shrink-0 mt-1 transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-slate-700 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Related landings (internal linking for SEO) */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="uppercase tracking-widest text-xs text-slate-500 mb-2">
          {lang === "it" ? "Altri servizi NCC in Gallura" : "Other NCC services in Gallura"}
        </div>
        <h2 className="font-display text-3xl brand-text mb-8">
          {lang === "it"
            ? "Scopri le nostre destinazioni"
            : "Discover our destinations"}
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {related.map((r) => {
            const rc = lang === "it" ? r.it : r.en;
            const rName = rc.h1.split("—")[0].trim();
            return (
              <Link
                key={r.slug}
                to={r.urlPath}
                data-testid={`loc-related-${r.slug}`}
                className="border border-slate-200 rounded-xl bg-white p-5 hover:border-slate-400 transition-colors flex items-center justify-between gap-3"
              >
                <div>
                  <div className="font-display text-lg brand-text">{rName}</div>
                  <div className="text-xs text-slate-500 mt-1">
                    {lang === "it" ? "Servizio NCC dedicato" : "Dedicated NCC service"}
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 shrink-0 brand-text" />
              </Link>
            );
          })}
          {/* Link back to Santa Teresa page */}
          <Link
            to="/ncc-santa-teresa-gallura"
            data-testid="loc-related-sts"
            className="border border-slate-200 rounded-xl bg-white p-5 hover:border-slate-400 transition-colors flex items-center justify-between gap-3"
          >
            <div>
              <div className="font-display text-lg brand-text">
                Santa Teresa Gallura
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {lang === "it" ? "Servizio NCC dedicato" : "Dedicated NCC service"}
              </div>
            </div>
            <ChevronRight className="w-5 h-5 shrink-0 brand-text" />
          </Link>
        </div>
      </section>

      {/* Final CTA */}
      <section className="brand-bg text-white">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h2 className="font-display text-3xl sm:text-4xl mb-4">
            {c.finalTitle}
          </h2>
          <p className="text-white/80 max-w-xl mx-auto mb-8">{c.finalDesc}</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a
              href="tel:+393470011246"
              data-testid="loc-final-call"
              className="inline-flex items-center gap-2 bg-white brand-text rounded-full px-6 h-12 hover:opacity-90 transition-opacity font-medium"
            >
              <Phone className="w-4 h-4" />
              +39 347 001 1246
            </a>
            <a
              href="https://wa.me/393470011246"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="loc-final-whatsapp"
              className="inline-flex items-center gap-2 border border-white/40 text-white rounded-full px-6 h-12 hover:bg-white/10 transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </a>
            <Link
              to="/#booking"
              data-testid="loc-final-book"
              className="inline-flex items-center gap-2 accent-bg text-[#0A2463] rounded-full px-6 h-12 hover:opacity-90 transition-opacity font-medium"
            >
              {lang === "it" ? "Prenota ora" : "Book now"}
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
